/**
 * EAN-13 encoder and renderer.
 *
 * Written out rather than pulled from a package: the whole symbology is three code tables, a
 * parity table and a checksum, and a till scanner reading the wrong bars is not a failure mode
 * worth importing. Output is vector, so it stays sharp at whatever width the screen gives it, and
 * bars are drawn with `crispEdges` because an antialiased bar edge is what makes a scanner hesitate.
 */

const LEFT_ODD = [
	"0001101",
	"0011001",
	"0010011",
	"0111101",
	"0100011",
	"0110001",
	"0101111",
	"0111011",
	"0110111",
	"0001011",
];
const LEFT_EVEN = [
	"0100111",
	"0110011",
	"0011011",
	"0100001",
	"0011101",
	"0111001",
	"0000101",
	"0010001",
	"0001001",
	"0010111",
];
const RIGHT = [
	"1110010",
	"1100110",
	"1101100",
	"1000010",
	"1011100",
	"1001110",
	"1010000",
	"1000100",
	"1001000",
	"1110100",
];

/** The first digit is not encoded as bars; it picks the odd/even pattern of the left half. */
const PARITY = [
	"OOOOOO",
	"OOEOEE",
	"OOEEOE",
	"OOEEEO",
	"OEOOEE",
	"OEEOOE",
	"OEEEOO",
	"OEOEOE",
	"OEOEEO",
	"OEEOEO",
];

/** Module geometry: 9 quiet + 95 symbol + 7 quiet, in bar-width units. */
const QUIET_LEFT = 9;
const WIDTH = 111;
const BAR_TOP = 6;
const BAR_HEIGHT = 72;
/** Guard bars run past the data bars into the digit row, as printed on every retail package. */
const GUARD_HEIGHT = 82;
const HEIGHT = 104;
const GUARD_BARS = [0, 2, 46, 48, 92, 94];

export function ean13Checksum(digits: string): number | null {
	if (!/^\d{12,13}$/.test(digits)) return null;
	let sum = 0;
	for (let i = 0; i < 12; i++) {
		const digit = digits.charCodeAt(i) - 48;
		sum += i % 2 === 0 ? digit : digit * 3;
	}
	return (10 - (sum % 10)) % 10;
}

type Run = { x: number; width: number; guard: boolean };

/** `null` when `value` is not a checksum-valid EAN-13, which is a state the UI has to show. */
function encode(value: string): Run[] | null {
	if (!/^\d{13}$/.test(value)) return null;
	const check = ean13Checksum(value);
	if (check === null || check !== value.charCodeAt(12) - 48) return null;

	const parity = PARITY[value.charCodeAt(0) - 48];
	if (parity === undefined) return null;

	let modules = "101";
	for (let i = 1; i <= 12; i++) {
		const digit = value.charCodeAt(i) - 48;
		const table = i <= 6 ? (parity[i - 1] === "O" ? LEFT_ODD : LEFT_EVEN) : RIGHT;
		const pattern = table[digit];
		if (pattern === undefined) return null;
		if (i === 7) modules += "01010";
		modules += pattern;
	}
	modules += "101";

	const runs: Run[] = [];
	let index = 0;
	while (index < modules.length) {
		if (modules[index] !== "1") {
			index++;
			continue;
		}
		const start = index;
		while (modules[index] === "1") index++;
		runs.push({ x: start, width: index - start, guard: GUARD_BARS.includes(start) });
	}
	return runs;
}

export function Ean13({ value }: { value: string }) {
	const runs = encode(value);
	if (runs === null) {
		return (
			<div className="barcode-invalid" role="alert">
				<strong>{value}</strong> is not a valid EAN-13 card number, so no barcode is drawn. Read the digits to the
				cashier instead.
			</div>
		);
	}

	const left = value.slice(1, 7);
	const right = value.slice(7);

	return (
		<svg
			className="barcode"
			viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
			preserveAspectRatio="xMidYMid meet"
			role="img"
			aria-label={`Bonus card ${value.split("").join(" ")}`}
		>
			<rect x="0" y="0" width={WIDTH} height={HEIGHT} fill="#ffffff" />
			<g fill="#000000" shapeRendering="crispEdges">
				{runs.map((run) => (
					<rect
						key={run.x}
						x={QUIET_LEFT + run.x}
						y={BAR_TOP}
						width={run.width}
						height={run.guard ? GUARD_HEIGHT : BAR_HEIGHT}
					/>
				))}
			</g>
			<g fill="#000000" fontFamily="ui-monospace, SFMono-Regular, monospace" fontSize="12" letterSpacing="0.6">
				<text x={QUIET_LEFT - 2} y={HEIGHT - 5} textAnchor="end">
					{value.slice(0, 1)}
				</text>
				<text x={QUIET_LEFT + 3 + 21} y={HEIGHT - 5} textAnchor="middle">
					{left}
				</text>
				<text x={QUIET_LEFT + 50 + 21} y={HEIGHT - 5} textAnchor="middle">
					{right}
				</text>
			</g>
		</svg>
	);
}
